<template>
  <div class="create-task-modal">
    <p v-if="helpText" class="create-task-modal__help">{{ helpText }}</p>

    <TextInput
      id="create-task-title"
      :label="$t('tasks_view.fields.name')"
      v-model="form.title"
      :placeholder="titlePlaceholder"
      :rules="['required']"
      autofocus
    />

    <TextInput
      id="create-task-description"
      :label="$t('tasks_view.fields.description')"
      v-model="form.description"
      :placeholder="$t('tasks_view.description_placeholder')"
    />

    <div class="create-task-modal__row">
      <SelectInput
        id="create-task-type"
        :label="$t('tasks_view.fields.type')"
        v-model="form.task_type"
        :options="typeOptions"
      />
      <SelectInput
        id="create-task-priority"
        :label="$t('tasks_view.fields.priority')"
        v-model="form.priority"
        :options="priorityOptions"
      />
    </div>

    <div class="create-task-modal__row">
      <TextInput
        id="create-task-due-date"
        :label="$t('tasks_view.fields.due_date')"
        v-model="form.due_date"
        type="date"
        :rules="['required']"
      />
      <TextInput
        id="create-task-due-time"
        :label="$t('tasks_view.fields.due_time')"
        v-model="form.due_time"
        type="time"
      />
    </div>

    <SelectInput
      id="create-task-assignee"
      :label="$t('tasks_view.fields.assigned_to')"
      v-model="form.assigned_to"
      :options="memberOptions"
      :placeholder="$t('tasks_view.unassigned')"
      :disabled="loadingMembers"
    />

    <div class="create-task-modal__actions">
      <Btn :text="$t('common.cancel')" variant="secondary" @click="close" />
      <Btn
        :text="submitLabel"
        variant="primary"
        :loading="saving"
        :disabled="!canSubmit"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import { taskService } from '@/services/taskService';
  import OrganizationService from '@/services/organizationService';
  import { useUserStore } from '@/store/userStore';
  import { useToastStore } from '@/store/toastStore';
  import { useUIStore } from '@/store/uiStore';

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  export default {
    name: 'CreateTaskModal',
    components: { Btn, TextInput, SelectInput },
    props: {
      modalId: { type: [String, Number], required: true },
      onSubmit: { type: Function, default: null },
      /** 'deal' | 'project' | 'general' */
      context: { type: String, default: 'general' },
      dealId: { type: String, default: null },
      projectId: { type: String, default: null },
      /** Pre-select assignee (user id) */
      defaultAssignedTo: { type: String, default: null },
      source: { type: String, default: 'manual' },
      parentTaskId: { type: String, default: null },
      initialTitle: { type: String, default: '' },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const userStore = useUserStore();
      const toastStore = useToastStore();
      const uiStore = useUIStore();
      const organizationService = new OrganizationService();

      const saving = ref(false);
      const loadingMembers = ref(false);
      const members = ref([]);

      const form = ref({
        title: props.initialTitle || '',
        description: '',
        task_type: 'general',
        priority: 'medium',
        due_date: getTodayDate(),
        due_time: '',
        assigned_to: props.defaultAssignedTo || userStore.user?.id || '',
      });

      const typeOptions = computed(() => [
        { value: 'general', label: $t('deals.task_type_general') },
        { value: 'callback', label: $t('deals.task_type_callback') },
        { value: 'follow_up', label: $t('deals.task_type_follow_up') },
      ]);

      const priorityOptions = computed(() => [
        { value: 'low', label: $t('deals.priority_low') },
        { value: 'medium', label: $t('deals.priority_medium') },
        { value: 'high', label: $t('deals.priority_high') },
      ]);

      const memberOptions = computed(() =>
        members.value
          .map((member) => {
            const user = member.user || member;
            const id = user?.id || member.user_id || member.id;
            const label =
              [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
              user?.name ||
              user?.email ||
              member.email ||
              id;
            return { value: id, label };
          })
          .filter((m) => m.value)
      );

      const helpText = computed(() => {
        if (props.context === 'project') return $t('tasks_view.help_project');
        if (props.context === 'deal') return $t('tasks_view.help_deal');
        return null;
      });

      const titlePlaceholder = computed(() => $t('deals.task_placeholder'));

      const submitLabel = computed(() => $t('tasks_view.create_task'));

      const canSubmit = computed(() => !!form.value.title?.trim() && !!form.value.due_date);

      const fetchMembers = async () => {
        const organizationId = userStore.currentOrganization?.id;
        if (!organizationId) return;
        loadingMembers.value = true;
        try {
          const response = await organizationService.getMembers(organizationId);
          members.value = Array.isArray(response) ? response : response?.data || [];
        } catch (error) {
          console.error('Failed to fetch members for task modal', error);
          members.value = [];
        } finally {
          loadingMembers.value = false;
        }
      };

      const close = () => {
        uiStore.closeModal(props.modalId);
      };

      const handleSubmit = async () => {
        if (!canSubmit.value || saving.value) return;
        saving.value = true;
        try {
          const payload = {
            title: form.value.title.trim(),
            description: form.value.description?.trim() || null,
            task_type: form.value.task_type || 'general',
            priority: form.value.priority || 'medium',
            due_date: form.value.due_date,
            due_time: form.value.due_time || null,
            assigned_to: form.value.assigned_to || null,
            source: props.source || 'manual',
            parent_task_id: props.parentTaskId || null,
          };

          let task;
          if (props.context === 'project' && props.projectId) {
            task = await taskService.createProjectTask(props.projectId, payload);
          } else if (props.context === 'deal' && props.dealId) {
            task = await taskService.createDealTask(props.dealId, payload);
          } else {
            if (props.dealId) payload.deal_id = props.dealId;
            if (props.projectId) payload.project_id = props.projectId;
            task = await taskService.createTask(payload);
          }

          toastStore.addToast({
            type: 'success',
            message: $t('deals.task_created'),
          });

          if (typeof props.onSubmit === 'function') {
            await props.onSubmit(task);
          } else {
            close();
          }
        } catch (error) {
          console.error('Failed to create task', error);
          toastStore.addToast({
            type: 'error',
            message: $t('deals.task_create_failed'),
          });
        } finally {
          saving.value = false;
        }
      };

      onMounted(fetchMembers);

      return {
        form,
        saving,
        loadingMembers,
        memberOptions,
        typeOptions,
        priorityOptions,
        helpText,
        titlePlaceholder,
        submitLabel,
        canSubmit,
        close,
        handleSubmit,
      };
    },
  };
</script>

<style scoped lang="scss">
  .create-task-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__help {
      margin: 0;
      @include fontSize('sm');
      color: var(--slate-600);
    }

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      margin-top: 0.5rem;
    }
  }
</style>
